import { Segmented, Spin, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { userManager } from '@/Core/Managers'
import { AccessDenied } from '@/Ship/Components/AccessDenied'
import { EntityLayout } from '@/Ship/Components/EntityCard'
import { classNames } from '@/Ship/Helpers/classNames'

import { diskAdminAuditUrl, diskAdminOverviewUrl, diskAdminPolicyUrl, diskAdminStoragesUrl } from '../Helpers/diskAdminRoutes'
import { CheckDiskAdminAccessTask } from '../Tasks/CheckDiskAdminAccessTask'

import type { ReactNode } from 'react'

import styles from './DiskAdmin.module.css'

export type DiskAdminTab = 'overview' | 'storages' | 'audit' | 'policy'

const TABS: ReadonlyArray<{ value: DiskAdminTab; label: string; url: () => string }> = [
    { value: 'overview', label: 'Огляд', url: diskAdminOverviewUrl },
    { value: 'storages', label: 'Диски', url: () => diskAdminStoragesUrl() },
    { value: 'audit', label: 'Журнал', url: () => diskAdminAuditUrl() },
    { value: 'policy', label: 'Налаштування', url: diskAdminPolicyUrl },
]

export const DISK_ADMIN_TITLE = 'Адміністрування диска'

interface DiskAdminShellProps {
    tab: DiskAdminTab
    /** Заголовок вкладки браузера; без нього — назва панелі. */
    documentTitle?: string
    /** Підзаголовок під назвою панелі. */
    eyebrow?: ReactNode
    children: ReactNode
}

/**
 * Каркас сторінок `/disk/admin/*` (`DISK-006` §5.1–5.2): назва панелі,
 * вкладки-сегменти (стан — в адресі), перевірка права.
 *
 * Доступ — за правом `DiskAdmin` з бека, тим самим, що ховає пункт меню
 * (`GATED_MENU_ITEMS['Disk.Admin']`). Перевірка — у сторінці, а не в
 * `pageConfig.accessFn`: маршрутний `accessFn` ядро малює без меню й шапки
 * (як і «Довідники»). Поки права не завантажені — спінер, а не «Відсутній
 * доступ» на мить.
 */
export const DiskAdminShell = observer(({ tab, documentTitle, eyebrow, children }: DiskAdminShellProps) => {
    const navigate = useNavigate()
    const { pathname, search } = useLocation()
    const permissionsLoaded = Boolean(userManager.user?.['permissions'])
    const allowed = permissionsLoaded && CheckDiskAdminAccessTask()

    useEffect(() => {
        document.title = documentTitle ?? DISK_ADMIN_TITLE
    }, [documentTitle])

    if (!permissionsLoaded) {
        return (
            <div style={{ padding: 48, textAlign: 'center' }}>
                <Spin />
            </div>
        )
    }

    if (!allowed) return <AccessDenied />

    return (
        <div className={classNames(styles['page'])} data-disk-admin={tab}>
            <EntityLayout>
                <header className={classNames(styles['head'])}>
                    <div className={classNames(styles['headTitle'])}>
                        <Typography.Title level={3} style={{ margin: 0 }}>
                            {DISK_ADMIN_TITLE}
                        </Typography.Title>
                        {eyebrow ? <div className={classNames(styles['eyebrow'])}>{eyebrow}</div> : null}
                    </div>
                    <div className={classNames(styles['tabs'])}>
                        <Segmented
                            aria-label="Розділ адміністрування диска"
                            data-testid="disk-admin-tabs"
                            value={tab}
                            onChange={(value) => {
                                const target = TABS.find((item) => item.value === value)

                                if (target) navigate(target.url())
                            }}
                            options={TABS.map((item) => ({
                                value: item.value,
                                // Клік на вже вибрану вкладку (з картки диска чи зі списку з
                                // фільтром) `onChange` не дає — веде на її початок підпис
                                label: (
                                    <span
                                        onClick={() => {
                                            if (item.value === tab && `${pathname}${search}` !== item.url()) {
                                                navigate(item.url())
                                            }
                                        }}
                                    >
                                        {item.label}
                                    </span>
                                ),
                            }))}
                        />
                    </div>
                </header>
                {children}
            </EntityLayout>
        </div>
    )
})
